import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import { LogOut, Trash2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import usePageMeta from "@/hooks/usePageMeta";
import { API } from "@/lib/site";
import { Logo } from "@/components/site/Header";

export const http = axios.create({ baseURL: API, withCredentials: true });
http.interceptors.request.use((c) => {
  const t = sessionStorage.getItem("tsr_token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

const googleSignIn = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/auth/login", { email, password });
      sessionStorage.setItem("tsr_token", data?.token ?? "");
      onLogin(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-sm bg-white border border-slate-200 p-8 chamfer-sm">
      <h1 className="font-display text-2xl font-bold">Admin sign in</h1>
      <button type="button" data-testid="admin-google-login-btn" onClick={googleSignIn}
        className="mt-6 w-full h-11 rounded-full border border-slate-300 bg-white font-semibold inline-flex items-center justify-center gap-2.5 hover:border-slate-500 transition-colors">
        <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
        Continue with Google
      </button>
      <div className="mt-6 flex items-center gap-3 text-xs text-slate-500"><span className="h-px flex-1 bg-slate-200" />or use email<span className="h-px flex-1 bg-slate-200" /></div>
      <div className="mt-6"><Label htmlFor="a-email">Email</Label><Input id="a-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-11" /></div>
      <div className="mt-4"><Label htmlFor="a-pass">Password</Label><Input id="a-pass" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-11" /></div>
      <button data-testid="admin-login-btn" disabled={busy} className="mt-6 w-full h-11 rounded-full bg-[#0e1116] text-white font-semibold inline-flex items-center justify-center gap-2">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
      </button>
    </form>
  );
};

const STATUS_CLS = { new: "bg-amber-100 text-amber-900", contacted: "bg-sky-100 text-sky-900", closed: "bg-slate-200 text-slate-800" };

const QuoteCard = ({ q, onStatus, onDelete }) => {
  const digits = (q.phone || "").replace(/\D/g, "");
  return (
    <article data-testid={`admin-quote-${q.id}`} className="bg-white border border-slate-200 p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-lg font-semibold">{q.name} <span className="text-slate-500 font-normal text-sm">· {q.service}</span></p>
          <p className="text-xs text-slate-600 font-mono-tech mt-1">{new Date(q.created_at).toLocaleString()} · via {q.contact_via}</p>
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_CLS[q.status]}`}>{q.status}</span>
      </div>
      <p className="mt-3 text-sm text-slate-800 whitespace-pre-wrap">{q.message}</p>
      <p className="mt-2 text-sm text-slate-600">{[q.material && `Material: ${q.material}`, q.quantity && `Qty/size: ${q.quantity}`].filter(Boolean).join(" · ")}</p>
      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
        <a data-testid={`admin-wa-${q.id}`} href={`https://wa.me/${digits}`} target="_blank" rel="noopener noreferrer" className="px-3 h-9 inline-flex items-center rounded-full bg-[#25D366] text-[#0b2e17] font-semibold">WhatsApp {q.phone}</a>
        {q.email && <a href={`mailto:${q.email}`} className="px-3 h-9 inline-flex items-center rounded-full border border-slate-300">{q.email}</a>}
        <select data-testid={`admin-status-${q.id}`} value={q.status} onChange={(e) => onStatus(q.id, e.target.value)} className="h-9 border border-slate-300 rounded-full px-3 bg-white">
          <option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option>
        </select>
        <button data-testid={`admin-delete-${q.id}`} onClick={() => onDelete(q.id)} className="h-9 w-9 grid place-items-center rounded-full border border-slate-300 hover:bg-red-50" aria-label="Delete request"><Trash2 className="h-4 w-4" /></button>
      </div>
    </article>
  );
};

export default function Admin() {
  usePageMeta("Admin | TSR Industry", "TSR Industry quotation requests", true);
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user ?? null);
  const [quotes, setQuotes] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/quotes");
      setQuotes(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) setUser(false);
    }
  }, []);

  useEffect(() => {
    if (location.state?.user) return;
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, [location.state]);
  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const onStatus = async (id, status) => {
    try { await http.patch(`/quotes/${id}`, { status }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try { await http.delete(`/quotes/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    sessionStorage.removeItem("tsr_token");
    setUser(false);
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#f6f4ef]">
      <header className="bg-[#0e1116] text-white">
        <div className="mx-auto max-w-5xl px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link"><Logo className="h-10 w-10" /><span className="font-display font-semibold">TSR Industry · Quotes</span></Link>
          {user && <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 text-sm"><LogOut className="h-4 w-4" /> Sign out</button>}
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-5 py-10">
        {user === null && <Loader2 className="h-6 w-6 animate-spin mx-auto" />}
        {user === false && <div className="grid place-items-center py-10"><Login onLogin={setUser} /></div>}
        {user && (
          <>
            <h1 className="font-display text-3xl font-bold">Quotation requests <span className="text-slate-500 text-xl" data-testid="admin-quote-count">({quotes.length})</span></h1>
            <div className="mt-6 space-y-4">
              {quotes.length === 0 && <p className="text-slate-600" data-testid="admin-empty">No requests yet.</p>}
              {quotes.map((q) => <QuoteCard key={q.id} q={q} onStatus={onStatus} onDelete={onDelete} />)}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
